import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Bars3BottomLeftIcon, MegaphoneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button, TopNavigation } from '@nerdunited-give/component-library';

import AvatarMenu from '../User/AvatarMenu';
import { useRouter } from 'next/router';
import Link from '../Link';
import { routes } from '~src/routes/routes';
import PurchaseNodePrompt from '~src/components/Blockchain/PurchaseNodePrompt';
import ReferralDropDown from '~src/components/Dashboard/ReferralDropDown';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function DashboardLayout({
  navigationItems = [],
  pageTitle,
  subNavItems,
  actionArea,
  children,
}) {
  const router = useRouter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);

  useEffect(() => {
    let entryIndex = navigationItems.findIndex((entry) => {
      return entry.href == router.pathname;
    });

    if (entryIndex === -1) {
      // No exact match found. Running loose check
      let bestMatchLengthDiff = null;
      for (let oneEntryIndex in navigationItems) {
        let entry = navigationItems[oneEntryIndex];
        if (router.pathname?.indexOf(entry.href) === 0) {
          let diff = router.pathname?.length - entry.href.length;
          if (bestMatchLengthDiff === null || diff < bestMatchLengthDiff) {
            bestMatchLengthDiff = diff;
            entryIndex = parseInt(oneEntryIndex);
          }
        }
      }
    }

    if (entryIndex >= 0) {
      setCurrentIndex(entryIndex);
    }
  }, [router.pathname]);

  const handleDonateClick = (e) => {
    e.preventDefault();
    router.push('/gifts/select');
  };

  const handleSubMenuItemClick = (e) => {
    e.preventDefault();
    router.push(e.target.href);
  }

  const handleReferralClick = (e) => {
    e.preventDefault();
    router.push(routes.referFriend.path);
  };

  if (actionArea == null) {
    actionArea = (
      <div className="flex flex-row gap-2">
        <div className="flex justify-center items-center mr-2">
          <ReferralDropDown />
        </div>
        <Button size="sm" onClick={handleDonateClick}>Donate</Button>
      </div>
    );
  }

  return (
    <div className="w-full h-auto bg-gray-200">
      <div className="dashboard-layout-container min-h-screen h-full">
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative z-40 lg:hidden" onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-giveGray-600 bg-opacity-75" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white pt-5 pb-4">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute top-0 right-0 -mr-12 pt-2">
                      <button
                        type="button"
                        className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex flex-shrink-0 items-center px-4">
                    <Link href="https://www.lemonaide.co/" className="mb-2 h-8">
                      <img src="/images/lemonaide-logo.svg" className="h-10" />
                    </Link>
                  </div>
                  <div className="mt-5 h-0 flex-1 overflow-y-auto">
                    <nav className="space-y-1 px-2">
                      {navigationItems.map((item, index) => (
                        <Link
                          key={item.id}
                          data-brand-qa={`main-nav-mobile-item-${item.id}`}
                          href={item.href}
                          className={classNames(
                            index === currentIndex
                              ? 'bg-mint-300 text-brandGray-900'
                              : 'text-brandGray-900 hover:bg-giveGray-50 hover:text-brandGray-900',
                            'group flex items-center px-2 py-2 text-base font-medium rounded-md'
                          )}
                        >
                          <item.icon
                            className={classNames(
                              index === currentIndex
                                ? 'text-brandGray-900'
                                : 'text-brandGray-900 group-hover:text-giveGray-500',
                              'mr-4 flex-shrink-0 h-6 w-6'
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      ))}
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
              <div className="w-14 flex-shrink-0" aria-hidden="true">
                {/* Dummy element to force sidebar to shrink to fit close icon */}
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex flex-grow flex-col overflow-y-auto bg-white pt-5 border-r border-giveGray-200 border-solid">
            <div className="flex flex-shrink-0 items-center px-4">
              <Link href="https://www.lemonaide.co/" className="mb-2 h-8">
                <img src="/images/lemonaide-logo.svg" className="h-10" />
              </Link>
            </div>
            <div className="mt-5 flex flex-grow flex-col">
              <nav className="flex-1 space-y-1 px-2 pb-4">
                {navigationItems.map((item, index) => (
                  <Link
                    key={item.id}
                    data-brand-qa={`main-nav-item-${item.id}`}
                    href={item.href}
                    className={classNames(
                      index === currentIndex
                        ? 'bg-mint-300 text-brandGray-900'
                        : 'text-brandGray-900 hover:bg-giveGray-50 hover:text-giveGray-500',
                      'group flex items-center px-2 py-2 text-sm font-medium rounded-md'
                    )}
                  >
                    <item.icon
                      className={classNames(
                        index === currentIndex
                          ? 'text-brandGray-900'
                          : 'text-brandGray-900 group-hover:text-giveGray-500',
                        'mr-3 flex-shrink-0 h-6 w-6'
                      )}
                      aria-hidden="true"
                    />
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col lg:pl-64">
          <div className="sticky top-0 z-10 flex h-fit flex-shrink-0 shadow">
            <button
              type="button"
              data-brand-qa="main-nav-mobile-menu-btn"
              className="border-r border-giveGray-200 bg-white px-4 text-giveGray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-200 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <TopNavigation
              className="bg-white"
              title={pageTitle}
              items={subNavItems}
              onItemClick={handleSubMenuItemClick}
              actionArea={actionArea}
              avatarMenu={<AvatarMenu disabled={navigationItems.length === 0}/>}
            />
          </div>

          <main className="flex-1 mb-16">
            <PurchaseNodePrompt />
            <div className="py-6 px-6">{children}</div>
          </main>
        </div>
      </div>
    </div>
  );
}
